import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import GisLayerMappings from './superadmin/GisLayerMappings';
import TermsManagement from './superadmin/TermsManagement';
import EmailTemplateManagement from './superadmin/EmailTemplateManagement';
import { styles } from './superadmin/superadminStyles';

const SuperAdminDashboard = () => {
    const navigate = useNavigate();

    // --- STATE ---
    const [logs, setLogs] = useState([]);
    
    // Data Stats
    const [events, setEvents] = useState([]);
    const [users, setUsers] = useState([]);
    const [clients, setClients] = useState([]);
    const [gisMappings, setGisMappings] = useState([]);

    // UI State
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [userClientFilter, setUserClientFilter] = useState('');
    const [eventClientFilter, setEventClientFilter] = useState('');
    const [selectedBidEventId, setSelectedBidEventId] = useState('');
    const [eventBids, setEventBids] = useState([]);

    // 👇 NEW: Demo Mode State
    const [demoMode, setDemoMode] = useState(localStorage.getItem('demoMode') === 'true');

    // 👇 NEW: Create Client Admin State
    const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
    const [newAdminForm, setNewAdminForm] = useState({ firstName: '', lastName: '', email: '', password: '', clientId: '' });
    const [showNewAdminPassword, setShowNewAdminPassword] = useState(false);
    const [createAdminError, setCreateAdminError] = useState('');
    const [createdAdminResult, setCreatedAdminResult] = useState(null);

    // 👇 NEW: Tenant Branding Manager State
    const [selectedBrandingClientId, setSelectedBrandingClientId] = useState('');
    const [brandingForm, setBrandingForm] = useState(null);

    // 👇 NEW: Create Client (Tenant Provisioning) State
    const [showCreateClientModal, setShowCreateClientModal] = useState(false);
    const [newClientForm, setNewClientForm] = useState({ name: '', slug: '', portalName: '', colors: { primary: '#2c3e50', secondary: '#27ae60' }, clientLogoUrl: '' });
    const [createClientError, setCreateClientError] = useState('');

    // --- LOGGING HELPER ---
    const addLog = useCallback((msg) => {
        setLogs(prev => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...prev]);
    }, []);

    // --- INITIAL DATA LOADING ---
    const loadEvents = useCallback(async () => {
        try {
            const res = await api.get('/superadmin/events');
            setEvents(res.data);
        } catch (err) { addLog("❌ Failed to load events"); }
    }, [addLog]);

    const loadUsers = useCallback(async () => {
        try {
            const res = await api.get('/superadmin/users');
            setUsers(res.data);
        } catch (err) { addLog("❌ Failed to load users"); }
    }, [addLog]);

    const loadClients = useCallback(async () => {
        try {
            const res = await api.get('/clients');
            setClients(res.data);
        } catch (err) { addLog("❌ Failed to load clients"); }
    }, [addLog]);

    const loadGisMappings = useCallback(async () => {
        try {
            const res = await api.get('/superadmin/gis-mappings');
            setGisMappings(res.data);
        } catch (err) { addLog("❌ Failed to load GIS layer mappings"); }
    }, [addLog]);

    useEffect(() => {
        loadEvents();
        loadUsers();
        loadClients();
        loadGisMappings();
    }, [loadEvents, loadUsers, loadClients, loadGisMappings]);

    // --- HANDLERS: USER MANAGEMENT ---
    const handleRoleChange = async (userId, newRole, currentName) => {
        if (!window.confirm(`Change ${currentName} to ${newRole}? Note: This triggers emails.`)) return;
        try {
            await api.put(`/superadmin/users/${userId}/role`, { role: newRole });
            addLog(`✅ Role updated for ${currentName}`);
            loadUsers();
        } catch (err) { addLog("❌ Role update failed: System must retain at least 1 Superadmin"); }
    };

    // 👇 UPDATED: Now requires a Reason
    const handleDeleteUser = async (userId, currentName) => {
        const reason = window.prompt(`⚠️ DESTROY USER: ${currentName}?\n\nREQUIRED: Enter reason for account deletion (this will be emailed to the user):`);
        
        if (!reason) return; // Cancel if no reason entered

        try {
            // Pass reason in the 'data' property for DELETE requests
            await api.delete(`/superadmin/users/${userId}`, { data: { reason } });
            addLog(`🗑️ User deleted: ${currentName}. Reason: ${reason}`);
            loadUsers();
        } catch (err) {
            addLog("❌ User delete failed: " + (err.response?.data?.msg || err.message));
        }
    };

    const toggleUser = (id) => {
        setSelectedUsers(prev => prev.includes(id) ? prev.filter(u => u !== id) : [...prev, id]);
    };

    const handleBulkDeleteUsers = async () => {
        if (selectedUsers.length === 0) return;
        const reason = window.prompt(`⚠️ DESTROY ${selectedUsers.length} USER(S)?\n\nREQUIRED: Enter reason for account deletion (this will be emailed to each user):`);
        if (!reason) return; // Cancel if no reason entered

        try {
            const res = await api.post('/superadmin/users/delete', { userIds: selectedUsers, reason });
            addLog(`🗑️ ${res.data.msg} Reason: ${reason}`);
            setSelectedUsers([]);
            loadUsers();
        } catch (err) {
            addLog("❌ Bulk user delete failed: " + (err.response?.data?.msg || err.message));
        }
    };

    // --- HANDLERS: CLIENT ADMIN BOOTSTRAPPING ---
    const openCreateAdminModal = () => {
        setNewAdminForm({ firstName: '', lastName: '', email: '', password: '', clientId: '' });
        setShowNewAdminPassword(false);
        setCreateAdminError('');
        setCreatedAdminResult(null);
        setShowCreateAdminModal(true);
    };

    const closeCreateAdminModal = () => {
        setShowCreateAdminModal(false);
    };

    const handleNewAdminFieldChange = (field, value) => {
        setNewAdminForm(prev => ({ ...prev, [field]: value }));
    };

    const handleCreateClientAdmin = async (e) => {
        e.preventDefault();
        setCreateAdminError('');

        const { firstName, lastName, email, password, clientId } = newAdminForm;
        if (!firstName || !lastName || !email || !password || !clientId) {
            setCreateAdminError('All fields are required.');
            return;
        }

        try {
            await api.post('/superadmin/users', { firstName, lastName, email, password, clientId });
            const clientName = clients.find(c => c._id === clientId)?.name || 'Unknown Client';
            addLog(`✅ Created Client Admin: ${firstName} ${lastName} (${clientName})`);
            setCreatedAdminResult({ firstName, lastName, email, password, clientName });
            loadUsers();
        } catch (err) {
            const msg = err.response?.data?.msg || 'Failed to create Client Admin';
            setCreateAdminError(msg);
            addLog(`❌ ${msg}`);
        }
    };

    // --- HANDLERS: TENANT BRANDING MANAGER ---
    const DEFAULT_BRANDING_COLORS = {
        primary: '#2c3e50',
        secondary: '#27ae60',
        accent: '#e67e22',
        background: '#f4f6f8',
        navbarBg: '#2c3e50',
        navbarText: '#ffffff',
    };

    const handleBrandingClientSelect = (e) => {
        const clientId = e.target.value;
        setSelectedBrandingClientId(clientId);

        if (!clientId) {
            setBrandingForm(null);
            return;
        }

        const client = clients.find(c => c._id === clientId);
        const branding = client?.branding || {};
        setBrandingForm({
            portalName: branding.portalName || '',
            clientLogoUrl: branding.clientLogoUrl || '',
            colors: { ...DEFAULT_BRANDING_COLORS, ...(branding.colors || {}) },
        });
    };

    const handleBrandingFieldChange = (field, value) => {
        setBrandingForm(prev => ({ ...prev, [field]: value }));
    };

    const handleBrandingColorChange = (colorKey, value) => {
        setBrandingForm(prev => ({ ...prev, colors: { ...prev.colors, [colorKey]: value } }));
    };

    // Shared by the Branding Manager's logo upload and the Create Client
    // modal's logo upload — reads a raw image file as a base64 data URL.
    // Returns false (and resets the file input) if the file was rejected.
    // 2MB raw-file cap: base64 encoding inflates payload size by ~33%, and the
    // backend JSON body limit is 10mb (see backend/server.js), so this keeps
    // the encoded payload comfortably under that ceiling.
    const readLogoAsBase64 = (e, onLoaded) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            e.target.value = '';
            addLog("❌ Error: Logo image must be smaller than 2MB");
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => onLoaded(reader.result);
        reader.readAsDataURL(file);
    };

    const handleLogoFileChange = (e) => {
        readLogoAsBase64(e, (dataUrl) => handleBrandingFieldChange('clientLogoUrl', dataUrl));
    };

    const handleSaveBranding = async (e) => {
        e.preventDefault();
        if (!selectedBrandingClientId || !brandingForm) return;

        const clientName = clients.find(c => c._id === selectedBrandingClientId)?.name || 'Unknown Client';

        try {
            const res = await api.put(`/superadmin/clients/${selectedBrandingClientId}`, { branding: brandingForm });
            setClients(prev => prev.map(c => c._id === selectedBrandingClientId ? res.data : c));
            addLog(`✅ Successfully updated branding for ${clientName}`);
        } catch (err) {
            addLog(`❌ Failed to update branding for ${clientName}: ` + (err.response?.data?.msg || err.message));
        }
    };

    // --- HANDLERS: CREATE CLIENT (TENANT PROVISIONING) ---
    // 🛡️ Client-side check only for fast UX feedback — the server re-validates
    // format/reserved-words/uniqueness as the real gate (backend/routes/clients.js).
    const SLUG_REGEX = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

    const openCreateClientModal = () => {
        setNewClientForm({ name: '', slug: '', portalName: '', colors: { primary: '#2c3e50', secondary: '#27ae60' }, clientLogoUrl: '' });
        setCreateClientError('');
        setShowCreateClientModal(true);
    };

    const closeCreateClientModal = () => {
        setShowCreateClientModal(false);
    };

    const handleNewClientFieldChange = (field, value) => {
        setNewClientForm(prev => ({ ...prev, [field]: value }));
    };

    const handleNewClientColorChange = (colorKey, value) => {
        setNewClientForm(prev => ({ ...prev, colors: { ...prev.colors, [colorKey]: value } }));
    };

    const handleNewClientLogoFileChange = (e) => {
        readLogoAsBase64(e, (dataUrl) => handleNewClientFieldChange('clientLogoUrl', dataUrl));
    };

    const handleCreateClient = async (e) => {
        e.preventDefault();
        setCreateClientError('');

        const { name, slug, portalName, colors, clientLogoUrl } = newClientForm;
        if (!name || !slug) {
            setCreateClientError('Client Name and Slug are required.');
            return;
        }

        if (!SLUG_REGEX.test(slug.toLowerCase().trim())) {
            setCreateClientError('Slug must be lowercase letters, numbers, and hyphens only (no leading/trailing hyphen).');
            return;
        }

        try {
            const res = await api.post('/clients', { name, slug, branding: { portalName, colors, clientLogoUrl } });
            setClients(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
            addLog(`✅ Created Client: ${res.data.name} (${res.data.slug})`);
            setShowCreateClientModal(false);
        } catch (err) {
            const msg = err.response?.data?.msg || 'Failed to create Client';
            setCreateClientError(msg);
            addLog(`❌ ${msg}`);
        }
    };

    // --- HANDLERS: GIS LAYER MAPPINGS ---
    // Throws on failure — GisLayerMappings' modal owns displaying the error
    // inline (same contract as AlgorithmLibrary's CSV upload handler).
    const handleSaveMapping = async (payload, existingId) => {
        if (existingId) {
            const res = await api.put(`/superadmin/gis-mappings/${existingId}`, payload);
            setGisMappings(prev => prev.map(m => m._id === existingId ? res.data : m));
            addLog(`✅ Updated GIS layer mapping: ${res.data.token}`);
        } else {
            const res = await api.post('/superadmin/gis-mappings', payload);
            setGisMappings(prev => [...prev, res.data].sort((a, b) => a.token.localeCompare(b.token)));
            addLog(`✅ Created GIS layer mapping: ${res.data.token}`);
        }
    };

    const handleDeleteMapping = async (id) => {
        try {
            await api.delete(`/superadmin/gis-mappings/${id}`);
            setGisMappings(prev => prev.filter(m => m._id !== id));
            addLog(`🗑️ GIS layer mapping deleted.`);
        } catch (err) {
            addLog("❌ Failed to delete GIS layer mapping: " + (err.response?.data?.msg || err.message));
        }
    };

    // --- HANDLERS: BID MANAGEMENT ---
    const handleBidEventSelect = async (e) => {
        const evId = e.target.value;
        setSelectedBidEventId(evId);
        setEventBids([]);
        if (!evId) return;

        try {
            const res = await api.get(`/superadmin/bids/${evId}`);
            setEventBids(res.data);
            addLog(`loaded ${res.data.length} bids for inspection.`);
        } catch (err) { addLog("❌ Failed to load bids"); }
    };

    const handleDeleteBid = async (bidId, amount) => {
        const reason = window.prompt(`Deleting Bid £${amount}.\n\nREQUIRED: Enter reason for deletion (sent to user):`);
        if (!reason) return;

        try {
            await api.delete(`/superadmin/bids/${bidId}`, { data: { reason } });
            addLog(`🗑️ Bid deleted. Reason: ${reason}`);
            // Refresh list
            const res = await api.get(`/superadmin/bids/${selectedBidEventId}`);
            setEventBids(res.data);
        } catch (err) { addLog("❌ Bid delete failed"); }
    };

    // --- HANDLERS: EXISTING FUNC ---
    const toggleEvent = (id) => {
        setSelectedEvents(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);
    };

    const deleteSelectedEvents = async () => {
        if (selectedEvents.length === 0) return;
        if (!window.confirm(`⚠️ DANGER: Deleting ${selectedEvents.length} Events.\n\nProceed?`)) return;
        try {
            await api.post('/superadmin/events/delete', { eventIds: selectedEvents });
            addLog(`🗑️ Deleted Events + Bids/Zones`);
            setSelectedEvents([]);
            loadEvents();
        } catch (err) { addLog("❌ Event delete failed"); }
    };

    // 👇 NEW: Toggle Demo Mode Handler
    const handleToggleDemoMode = () => {
        const newVal = !demoMode;
        setDemoMode(newVal);
        localStorage.setItem('demoMode', newVal);
        addLog(newVal ? "🎭 Demo Mode ENABLED (Masking Active)" : "👀 Demo Mode DISABLED (Real Data Shown)");
        
        // Force a page reload so React clears any real data currently held in its memory
        if(window.confirm(`${newVal ? 'Enable' : 'Disable'} Demo Mode?\n\nThe page will reload to clear memory and fetch the ${newVal ? 'masked' : 'real'} data.`)) {
            window.location.reload();
        } else {
            // Revert if they cancel the reload
            setDemoMode(!newVal);
            localStorage.setItem('demoMode', !newVal);
        }
    };

    // --- USER MANAGER: CLIENT NAME LOOKUP + FILTER ---
    const clientNameById = (id) => clients.find(c => c._id === id)?.name || '—';
    const filteredUsers = userClientFilter
        ? users.filter(u => u.clientId === userClientFilter)
        : users;
    const allFilteredSelected = filteredUsers.length > 0 && filteredUsers.every(u => selectedUsers.includes(u._id));

    // --- EVENT DELETE: CLIENT LABEL + FILTER/SORT ---
    // Global (clientId null) events sort/group first — matches the "Global"
    // label shown in the table, rather than falling through clientNameById's
    // '—' (that fallback means "unknown/deleted client", a different case).
    const eventClientLabel = (ev) => ev.clientId ? clientNameById(ev.clientId) : 'Global';
    const sortedFilteredEvents = events
        .filter(ev => !eventClientFilter || ev.clientId === eventClientFilter)
        .sort((a, b) => eventClientLabel(a).localeCompare(eventClientLabel(b)));
    const toggleSelectAllFiltered = () => {
        if (allFilteredSelected) {
            setSelectedUsers(prev => prev.filter(id => !filteredUsers.some(u => u._id === id)));
        } else {
            setSelectedUsers(prev => [...new Set([...prev, ...filteredUsers.map(u => u._id)])]);
        }
    };

    return (
        <div style={{ minHeight: '100vh', background: '#2c3e50', color: '#ecf0f1', padding: '40px' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
                    <h1 style={{ margin: 0 }}>🛡️ SuperAdmin Console</h1>
                    
                    {/* 👇 UPDATED: Added Flex container to hold both buttons */}
                    <div style={{ display: 'flex', gap: '15px' }}>
                        <button 
                            onClick={handleToggleDemoMode} 
                            style={{ padding: '10px 20px', background: demoMode ? '#f39c12' : 'transparent', color: 'white', border: `1px solid ${demoMode ? '#f39c12' : '#7f8c8d'}`, cursor: 'pointer', borderRadius: '4px', fontWeight: 'bold' }}
                        >
                            {demoMode ? '🎭 Demo Mode: ON' : '🎭 Demo Mode: OFF'}
                        </button>
                        
                        <button onClick={() => navigate('/admin')} style={{ padding: '10px 20px', background: 'transparent', color: 'white', border: '1px solid white', cursor: 'pointer', borderRadius: '4px' }}>
                            &larr; Back to Regular Admin
                        </button>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr', gap: '30px' }}>
                    
                    {/* --- CARD 1: USER MANAGEMENT --- */}
                    <div style={{ ...styles.card, gridColumn: '1 / -1' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2 style={{ marginTop: 0, color: '#3498db' }}>👥 User Manager</h2>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <select
                                    value={userClientFilter}
                                    onChange={(e) => setUserClientFilter(e.target.value)}
                                    style={{ background: '#34495e', color: 'white', border: '1px solid #7f8c8d', borderRadius: '4px', padding: '8px' }}
                                >
                                    <option value="">-- Filter by Client: All --</option>
                                    {clients.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                </select>
                                <button
                                    onClick={handleBulkDeleteUsers}
                                    disabled={selectedUsers.length === 0}
                                    style={{ ...styles.btn, background: selectedUsers.length > 0 ? '#c0392b' : '#7f8c8d', padding: '8px 14px', fontSize: '0.85rem' }}
                                >
                                    🗑️ Delete Selected ({selectedUsers.length})
                                </button>
                                <button
                                    onClick={openCreateAdminModal}
                                    style={{ ...styles.btn, background: '#27ae60', padding: '8px 14px', fontSize: '0.85rem' }}
                                >
                                    ➕ Create Client Admin
                                </button>
                            </div>
                        </div>
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                <thead>
                                    <tr style={{ background: '#2c3e50', color: '#bdc3c7', textAlign: 'left' }}>
                                        <th style={{padding:'8px'}}>
                                            <input
                                                type="checkbox"
                                                checked={allFilteredSelected}
                                                onChange={toggleSelectAllFiltered}
                                            />
                                        </th>
                                        <th style={{padding:'8px'}}>User</th>
                                        <th style={{padding:'8px'}}>Role</th>
                                        <th style={{padding:'8px'}}>Client</th>
                                        <th style={{padding:'8px'}}>Associated Clients</th>
                                        <th style={{padding:'8px'}}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map(u => (
                                        <tr key={u._id} style={{ borderBottom: '1px solid #7f8c8d' }}>
                                            <td style={{padding:'8px'}}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUsers.includes(u._id)}
                                                    onChange={() => toggleUser(u._id)}
                                                />
                                            </td>
                                            <td style={{padding:'8px'}}>
                                                <strong>{u.name}</strong><br/>
                                                <span style={{fontSize:'0.8rem', color:'#bdc3c7'}}>{u.email}</span>
                                            </td>
                                            <td style={{padding:'8px'}}>
                                                <select
                                                    value={u.role}
                                                    onChange={(e) => handleRoleChange(u._id, e.target.value, u.name)}
                                                    style={{background: '#34495e', color: 'white', border: '1px solid #7f8c8d', borderRadius: '4px'}}
                                                >
                                                    <option value="user">User</option>
                                                    <option value="admin">Admin</option>
                                                    <option value="superadmin">Super</option>
                                                </select>
                                            </td>
                                            <td style={{padding:'8px'}}>{u.clientId ? clientNameById(u.clientId) : '—'}</td>
                                            <td style={{padding:'8px', fontSize:'0.85rem'}}>
                                                {u.associatedClientIds && u.associatedClientIds.length > 0
                                                    ? u.associatedClientIds.map(clientNameById).join(', ')
                                                    : '—'}
                                            </td>
                                            <td style={{padding:'8px'}}>
                                                <button onClick={() => handleDeleteUser(u._id, u.name)} style={{background:'none', border:'none', cursor:'pointer'}}>❌</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* --- CARD 2: BID POLICE --- */}
                    <div style={styles.card}>
                        <h2 style={{ marginTop: 0, color: '#9b59b6' }}>🔨 Bid Force-Delete</h2>
                        <select
                            value={selectedBidEventId}
                            onChange={handleBidEventSelect}
                            style={{width: '100%', padding: '10px', marginBottom: '15px', background: '#34495e', color: 'white', border: '1px solid #7f8c8d'}}
                        >
                            <option value="">-- Select Event to Inspect --</option>
                            {events.map(ev => <option key={ev._id} value={ev._id}>{ev.name}</option>)}
                        </select>

                        <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                            {selectedBidEventId && eventBids.length === 0 && <p>No bids found.</p>}
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                <tbody>
                                    {eventBids.map(bid => (
                                        <tr key={bid._id} style={{ borderBottom: '1px solid #7f8c8d' }}>
                                            <td style={{padding:'8px'}}>
                                                <strong>{bid.parcel?.parcelRef}</strong><br/>
                                                <span style={{fontSize:'0.8rem', color:'#bdc3c7'}}>{bid.user?.name}</span>
                                            </td>
                                            <td>£{bid.amount}</td>
                                            <td>
                                                <button 
                                                    onClick={() => handleDeleteBid(bid._id, bid.amount)}
                                                    style={{background: '#c0392b', color: 'white', border:'none', borderRadius:'4px', padding:'2px 8px', fontSize:'0.8rem', cursor:'pointer'}}
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* --- CARD 4: BULK EVENTS (Existing) --- */}
                    <div style={styles.card}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2 style={{ marginTop: 0, color: '#f39c12' }}>📦 Event Delete</h2>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <select
                                    value={eventClientFilter}
                                    onChange={(e) => setEventClientFilter(e.target.value)}
                                    style={{ background: '#34495e', color: 'white', border: '1px solid #7f8c8d', borderRadius: '4px', padding: '8px' }}
                                >
                                    <option value="">-- Filter by Client: All --</option>
                                    {clients.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                </select>
                                <button
                                    onClick={deleteSelectedEvents}
                                    disabled={selectedEvents.length === 0}
                                    style={{ ...styles.btn, background: selectedEvents.length > 0 ? '#d35400' : '#7f8c8d', padding: '5px 10px', fontSize: '0.9rem' }}
                                >
                                    Execute ({selectedEvents.length})
                                </button>
                            </div>
                        </div>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '10px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                <thead>
                                    <tr style={{ background: '#2c3e50', color: '#bdc3c7', textAlign: 'left', position: 'sticky', top: 0 }}>
                                        <th style={{ padding: '6px' }}></th>
                                        <th style={{ padding: '6px' }}>Name</th>
                                        <th style={{ padding: '6px' }}>Client</th>
                                        <th style={{ padding: '6px' }}>Status</th>
                                        <th style={{ padding: '6px' }}>Archived</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedFilteredEvents.map(ev => (
                                        <tr key={ev._id} style={{ borderBottom: '1px solid #555' }}>
                                            <td style={{ padding: '6px' }}>
                                                <input type="checkbox" checked={selectedEvents.includes(ev._id)} onChange={() => toggleEvent(ev._id)} />
                                            </td>
                                            <td style={{ padding: '6px' }}>{ev.name}</td>
                                            <td style={{ padding: '6px' }}>{eventClientLabel(ev)}</td>
                                            <td style={{ padding: '6px', textTransform: 'capitalize' }}>{ev.status}</td>
                                            <td style={{ padding: '6px' }}>{ev.isArchived ? 'Yes' : 'No'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* --- CARD 5: TENANT BRANDING MANAGER --- */}
                    <div style={{ ...styles.card, gridColumn: '1 / -1' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2 style={{ marginTop: 0, color: '#1abc9c' }}>🏢 Tenant Branding Manager</h2>
                            <button
                                onClick={openCreateClientModal}
                                style={{ ...styles.btn, background: '#1abc9c', padding: '8px 14px', fontSize: '0.85rem' }}
                            >
                                ➕ Create Client
                            </button>
                        </div>

                        <select
                            value={selectedBrandingClientId}
                            onChange={handleBrandingClientSelect}
                            style={{ width: '100%', padding: '10px', marginBottom: '15px', background: '#2c3e50', color: 'white', border: '1px solid #7f8c8d' }}
                        >
                            <option value="">-- Select Client to Edit --</option>
                            {clients.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                        </select>

                        {brandingForm && (
                            <form onSubmit={handleSaveBranding}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                                    <div>
                                        <label style={styles.label}>Portal Name</label>
                                        <input
                                            type="text"
                                            value={brandingForm.portalName}
                                            onChange={(e) => handleBrandingFieldChange('portalName', e.target.value)}
                                            style={styles.input}
                                        />
                                    </div>
                                    <div>
                                        <label style={styles.label}>Client Logo</label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleLogoFileChange}
                                            style={styles.input}
                                        />
                                        {brandingForm.clientLogoUrl && (
                                            <img
                                                src={brandingForm.clientLogoUrl}
                                                alt="Logo preview"
                                                style={{ height: '28px', width: 'auto', marginTop: '8px', background: 'white', padding: '2px', borderRadius: '3px' }}
                                            />
                                        )}
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                                    {Object.keys(DEFAULT_BRANDING_COLORS).map(colorKey => (
                                        <div key={colorKey}>
                                            <label style={styles.label}>{colorKey}</label>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                <input
                                                    type="text"
                                                    value={brandingForm.colors[colorKey]}
                                                    onChange={(e) => handleBrandingColorChange(colorKey, e.target.value)}
                                                    style={{ ...styles.input, flex: 1 }}
                                                />
                                                <div style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    flexShrink: 0,
                                                    backgroundColor: brandingForm.colors[colorKey],
                                                    border: '1px solid #7f8c8d',
                                                    borderRadius: '3px',
                                                }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <button type="submit" style={{ ...styles.btn, background: '#1abc9c' }}>
                                        Save Branding
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>

                    {/* --- CARD 6: GIS LAYER MAPPINGS --- */}
                    <GisLayerMappings
                        mappings={gisMappings}
                        clients={clients}
                        handleSaveMapping={handleSaveMapping}
                        handleDeleteMapping={handleDeleteMapping}
                    />

                    {/* --- CARD 7: TERMS OF USE --- */}
                    <TermsManagement />

                    {/* --- CARD 8: EMAIL TEMPLATE MANAGEMENT --- */}
                    <EmailTemplateManagement />

                </div>

                {/* --- CONSOLE --- */}
                <div style={{ marginTop: '30px', fontFamily: 'monospace', background: 'black', padding: '15px', borderRadius: '5px', height: '150px', overflowY: 'auto' }}>
                    <div style={{ color: '#00ff00', fontWeight: 'bold', marginBottom: '5px' }}>root@elms-engine:~$ System Logs</div>
                    {logs.map((l, i) => <div key={i} style={{ color: '#ddd' }}>{l}</div>)}
                </div>

            </div>

            {/* --- MODAL: CREATE CLIENT ADMIN --- */}
            {showCreateAdminModal && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalBox}>
                        {!createdAdminResult ? (
                            <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                    <h2 style={{ margin: 0, color: '#27ae60' }}>➕ Create New Client Admin</h2>
                                    <button onClick={closeCreateAdminModal} style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.2rem', cursor: 'pointer' }}>✖</button>
                                </div>

                                <form onSubmit={handleCreateClientAdmin}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                                        <div>
                                            <label style={styles.label}>First Name</label>
                                            <input
                                                type="text"
                                                value={newAdminForm.firstName}
                                                onChange={(e) => handleNewAdminFieldChange('firstName', e.target.value)}
                                                style={styles.input}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label style={styles.label}>Last Name</label>
                                            <input
                                                type="text"
                                                value={newAdminForm.lastName}
                                                onChange={(e) => handleNewAdminFieldChange('lastName', e.target.value)}
                                                style={styles.input}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={styles.label}>Email</label>
                                        <input
                                            type="email"
                                            value={newAdminForm.email}
                                            onChange={(e) => handleNewAdminFieldChange('email', e.target.value)}
                                            style={styles.input}
                                            required
                                        />
                                    </div>

                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={styles.label}>Password</label>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <input
                                                type={showNewAdminPassword ? 'text' : 'password'}
                                                value={newAdminForm.password}
                                                onChange={(e) => handleNewAdminFieldChange('password', e.target.value)}
                                                style={{ ...styles.input, flex: 1 }}
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowNewAdminPassword(prev => !prev)}
                                                style={{ padding: '0 12px', background: '#7f8c8d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                            >
                                                {showNewAdminPassword ? '🙈 Hide' : '👁 Show'}
                                            </button>
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: '20px' }}>
                                        <label style={styles.label}>Client Organization</label>
                                        <select
                                            value={newAdminForm.clientId}
                                            onChange={(e) => handleNewAdminFieldChange('clientId', e.target.value)}
                                            style={styles.input}
                                            required
                                        >
                                            <option value="">-- Select Client --</option>
                                            {clients.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                                        </select>
                                    </div>

                                    {createAdminError && (
                                        <div style={{ color: '#e74c3c', marginBottom: '15px', fontSize: '0.9rem' }}>{createAdminError}</div>
                                    )}

                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                        <button type="button" onClick={closeCreateAdminModal} style={{ ...styles.btn, background: 'transparent', border: '1px solid #7f8c8d' }}>
                                            Cancel
                                        </button>
                                        <button type="submit" style={{ ...styles.btn, background: '#27ae60' }}>
                                            Create Admin
                                        </button>
                                    </div>
                                </form>
                            </>
                        ) : (
                            <>
                                <h2 style={{ marginTop: 0, color: '#27ae60' }}>✅ Client Admin Created</h2>
                                <p style={{ fontSize: '0.9rem', color: '#bdc3c7' }}>
                                    Copy these credentials now — the password is not stored anywhere retrievable.
                                </p>
                                <div style={styles.credentialsBox}>
                                    <div><strong>Name:</strong> {createdAdminResult.firstName} {createdAdminResult.lastName}</div>
                                    <div><strong>Client:</strong> {createdAdminResult.clientName}</div>
                                    <div><strong>Email:</strong> {createdAdminResult.email}</div>
                                    <div><strong>Password:</strong> {createdAdminResult.password}</div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                                    <button onClick={closeCreateAdminModal} style={{ ...styles.btn, background: '#27ae60' }}>
                                        Done
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* --- MODAL: CREATE CLIENT (TENANT PROVISIONING) --- */}
            {showCreateClientModal && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalBox}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2 style={{ margin: 0, color: '#1abc9c' }}>➕ Create New Client</h2>
                            <button onClick={closeCreateClientModal} style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.2rem', cursor: 'pointer' }}>✖</button>
                        </div>

                        <form onSubmit={handleCreateClient}>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={styles.label}>Client Name</label>
                                <input
                                    type="text"
                                    value={newClientForm.name}
                                    onChange={(e) => handleNewClientFieldChange('name', e.target.value)}
                                    style={styles.input}
                                    required
                                />
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <label style={styles.label}>Tenant Slug (subdomain)</label>
                                <input
                                    type="text"
                                    value={newClientForm.slug}
                                    onChange={(e) => handleNewClientFieldChange('slug', e.target.value)}
                                    placeholder="e.g. wessex-water"
                                    style={styles.input}
                                    required
                                />
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <label style={styles.label}>Portal Name</label>
                                <input
                                    type="text"
                                    value={newClientForm.portalName}
                                    onChange={(e) => handleNewClientFieldChange('portalName', e.target.value)}
                                    placeholder="e.g. Wessex Water Portal"
                                    style={styles.input}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                                <div>
                                    <label style={styles.label}>Primary Color</label>
                                    <input
                                        type="text"
                                        value={newClientForm.colors.primary}
                                        onChange={(e) => handleNewClientColorChange('primary', e.target.value)}
                                        style={styles.input}
                                    />
                                </div>
                                <div>
                                    <label style={styles.label}>Secondary Color</label>
                                    <input
                                        type="text"
                                        value={newClientForm.colors.secondary}
                                        onChange={(e) => handleNewClientColorChange('secondary', e.target.value)}
                                        style={styles.input}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={styles.label}>Client Logo</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleNewClientLogoFileChange}
                                    style={styles.input}
                                />
                                {newClientForm.clientLogoUrl && (
                                    <img
                                        src={newClientForm.clientLogoUrl}
                                        alt="Logo preview"
                                        style={{ height: '28px', width: 'auto', marginTop: '8px', background: 'white', padding: '2px', borderRadius: '3px' }}
                                    />
                                )}
                            </div>

                            {createClientError && (
                                <div style={{ color: '#e74c3c', marginBottom: '15px', fontSize: '0.9rem' }}>{createClientError}</div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button type="button" onClick={closeCreateClientModal} style={{ ...styles.btn, background: 'transparent', border: '1px solid #7f8c8d' }}>
                                    Cancel
                                </button>
                                <button type="submit" style={{ ...styles.btn, background: '#1abc9c' }}>
                                    Create Client
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SuperAdminDashboard;